import React from 'react';
import { Link } from 'react-router-dom';
import {
  buildBillingSmartFilters,
  rowMatchesBillingFilters,
  type BillingSmartFilter,
} from './billingSmartFilters';
import {
  inferEntitledTierId,
  PRICING_TIERS,
  type PricingTierId,
} from '../constants/pricingTiers';
import { getAttribution } from '../marketingAttribution';

export type BillingSubscriptionRow = {
  subscription_id: string;
  customer_id?: string;
  company_id?: number | null;
  company_name?: string;
  email?: string;
  tier_id?: string;
  status?: string;
  current_period_end?: string;
  cancel_at_period_end?: boolean;
  price_id?: string;
  created_at?: string;
  updated_at?: string;
  last_webhook_at?: string;
  last_webhook_type?: string;
  entitlement_sync_status?: string;
  entitlement_synced_at?: string;
  entitlement_features?: string[];
  live_company_features?: string[];
};

type BillingSummary = {
  total?: number;
  by_status?: Record<string, number>;
  active?: number;
  past_due?: number;
  canceled?: number;
  cancel_scheduled?: number;
};

type CustomerBillingPanelProps = {
  language?: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string | null;
  showAdminRevenue?: boolean;
  /** Effective feature keys from the signed-in profile (used when Stripe rows are absent). */
  effectiveFeatures?: string[] | null;
  accountEmail?: string | null;
  /** self-serve = subscriber billing; operator = inbound customer revenue. */
  audience?: 'self-serve' | 'operator';
};

const tierLabel = (tierId?: string) => {
  const tier = String(tierId || '').toLowerCase();
  if (tier === 'advanced') return 'CompliAdvantage';
  if (tier === 'basic') return 'CompliCore';
  if (tier === 'enterprise') return 'CompliComplete';
  return tierId || 'Plan';
};

const formatDate = (value?: string) => (value ? String(value).slice(0, 10) : '—');

const renewalLabel = (row: BillingSubscriptionRow, language: 'en' | 'es') => {
  const status = String(row.status || '').toLowerCase();
  if (row.cancel_at_period_end && (status === 'active' || status === 'trialing')) {
    return language === 'es' ? `Termina ${formatDate(row.current_period_end)}` : `Ends ${formatDate(row.current_period_end)}`;
  }
  if (!row.current_period_end) return '—';
  return language === 'es' ? `Renueva ${formatDate(row.current_period_end)}` : `Renews ${formatDate(row.current_period_end)}`;
};

const firstBillingEmail = (...lists: BillingSubscriptionRow[][]) => {
  for (const list of lists) {
    for (const row of list) {
      const email = String(row.email || '').trim();
      if (email) return email;
    }
  }
  return null;
};

const normalizeTierId = (tierId?: string | null): PricingTierId | null => {
  const tier = String(tierId || '').toLowerCase();
  if (tier === 'basic' || tier === 'advanced' || tier === 'enterprise') {
    return tier;
  }
  return null;
};

const CustomerBillingPanel: React.FC<CustomerBillingPanelProps> = ({
  language = 'en',
  gatewayBaseUrl,
  accessToken,
  showAdminRevenue = false,
  effectiveFeatures = null,
  accountEmail = null,
  audience,
}) => {
  const [rows, setRows] = React.useState<BillingSubscriptionRow[]>([]);
  const [mine, setMine] = React.useState<BillingSubscriptionRow[]>([]);
  const [summary, setSummary] = React.useState<BillingSummary | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [portalBusy, setPortalBusy] = React.useState(false);
  const [upgradeBusyId, setUpgradeBusyId] = React.useState<PricingTierId | null>(null);
  const [syncBusyId, setSyncBusyId] = React.useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [planFilter, setPlanFilter] = React.useState('all');
  const [timeframeFilter, setTimeframeFilter] = React.useState('all');
  const [syncFilter, setSyncFilter] = React.useState('all');
  const [endingSoon, setEndingSoon] = React.useState(false);
  const [cancelScheduled, setCancelScheduled] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [activeInsightId, setActiveInsightId] = React.useState<string | null>(null);
  const [plansOpen, setPlansOpen] = React.useState(false);
  const apiBase = String(gatewayBaseUrl || '').replace(/\/$/, '');

  const load = React.useCallback(async () => {
    if (!apiBase || !accessToken) {
      setError(language === 'es' ? 'Se requiere inicio de sesión.' : 'Sign-in required.');
      return;
    }
    try {
      const response = await fetch(`${apiBase}/api/v1/billing/subscriptions`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = await response.json();
      const subscriptions = Array.isArray(body?.subscriptions) ? body.subscriptions : [];
      setRows(subscriptions);
      setSummary(body?.summary || null);
      if (showAdminRevenue) {
        const mineResponse = await fetch(`${apiBase}/api/v1/billing/subscriptions?mine=1`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (mineResponse.ok) {
          const mineBody = await mineResponse.json();
          setMine(Array.isArray(mineBody?.subscriptions) ? mineBody.subscriptions : []);
        } else setMine([]);
      } else {
        setMine(subscriptions);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load billing');
    }
  }, [accessToken, apiBase, language, showAdminRevenue]);

  React.useEffect(() => { void load(); }, [load]);

  const openPortal = React.useCallback(async () => {
    if (!apiBase || !accessToken) return;
    setPortalBusy(true);
    setError(null);
    try {
      const response = await fetch(`${apiBase}/api/v1/billing/portal-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ return_url: `${window.location.origin}/console/billing` }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.detail || `HTTP ${response.status}`);
      if (body?.url) {
        window.location.assign(String(body.url));
        return;
      }
      throw new Error('Portal session did not return a URL');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setPortalBusy(false);
    }
  }, [accessToken, apiBase]);

  const startTierAction = React.useCallback(async (tierId: PricingTierId) => {
    if (!apiBase) return;
    if (tierId === 'enterprise') {
      window.location.assign('mailto:operations@nextcompli.com?subject=CompliComplete%20quote');
      return;
    }
    setUpgradeBusyId(tierId);
    setError(null);
    try {
      const email = String(
        accountEmail
        || firstBillingEmail(mine, rows)
        || '',
      ).trim().toLowerCase();
      if (email && accessToken) {
        const checkoutResponse = await fetch(`${apiBase}/api/v1/billing/checkout-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tier_id: tierId,
            email,
            success_url: `${window.location.origin}/console/billing?checkout=success`,
            cancel_url: `${window.location.origin}/console/billing?checkout=cancel`,
            ...getAttribution(),
          }),
        });
        const checkoutBody = await checkoutResponse.json().catch(() => ({}));
        if (checkoutResponse.ok && checkoutBody?.url) {
          window.location.assign(String(checkoutBody.url));
          return;
        }
      }
      await openPortal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start upgrade');
    } finally {
      setUpgradeBusyId(null);
    }
  }, [accessToken, accountEmail, apiBase, mine, openPortal, rows]);

  const syncEntitlements = React.useCallback(async (subscriptionId: string) => {
    if (!apiBase || !accessToken) return;
    setSyncBusyId(subscriptionId);
    try {
      const response = await fetch(`${apiBase}/api/v1/billing/sync-entitlements`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription_id: subscriptionId }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body?.detail || `HTTP ${response.status}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync entitlements');
    } finally {
      setSyncBusyId(null);
    }
  }, [accessToken, apiBase, load]);

  const clearFilters = React.useCallback(() => {
    setStatusFilter('all');
    setPlanFilter('all');
    setTimeframeFilter('all');
    setSyncFilter('all');
    setEndingSoon(false);
    setCancelScheduled(false);
    setQuery('');
    setActiveInsightId(null);
  }, []);

  const applyInsight = React.useCallback((insight: BillingSmartFilter) => {
    clearFilters();
    setActiveInsightId(insight.id);
    if (insight.patch.status) setStatusFilter(insight.patch.status);
    if (insight.patch.plan) setPlanFilter(insight.patch.plan);
    if (insight.patch.timeframe) setTimeframeFilter(insight.patch.timeframe);
    if (insight.patch.sync) setSyncFilter(insight.patch.sync);
    if (insight.patch.endingSoon) setEndingSoon(true);
    if (insight.patch.cancelScheduled) setCancelScheduled(true);
  }, [clearFilters]);

  const smartFilters = React.useMemo(() => buildBillingSmartFilters(rows), [rows]);
  const filteredAdminRows = React.useMemo(
    () => rows.filter((row) => rowMatchesBillingFilters(row, {
      status: statusFilter,
      plan: planFilter,
      timeframe: timeframeFilter,
      sync: syncFilter,
      endingSoon,
      cancelScheduled,
      query,
    })),
    [cancelScheduled, endingSoon, planFilter, query, rows, statusFilter, syncFilter, timeframeFilter],
  );

  const exportCsv = React.useCallback(() => {
    const header = ['company_name', 'email', 'tier_id', 'status', 'cancel_at_period_end', 'current_period_end', 'entitlement_sync_status', 'customer_id', 'subscription_id'];
    const escape = (value: unknown) => {
      const text = String(value ?? '');
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const lines = [header.join(',')];
    for (const row of filteredAdminRows) {
      lines.push([
        row.company_name, row.email, row.tier_id, row.status,
        row.cancel_at_period_end ? '1' : '0', row.current_period_end,
        row.entitlement_sync_status, row.customer_id, row.subscription_id,
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `nextcompli-customer-revenue-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }, [filteredAdminRows]);

  const planRows = showAdminRevenue ? mine : rows;
  const stripeTierId = normalizeTierId(planRows.find((row) => row.tier_id)?.tier_id);
  const entitledTierId = inferEntitledTierId(effectiveFeatures);
  const currentTierId = stripeTierId || entitledTierId;
  const currentTierLabel = currentTierId ? tierLabel(currentTierId) : null;
  const resolvedAudience = audience || (showAdminRevenue ? 'operator' : 'self-serve');
  const activeFilterChips: { id: string; label: string; clear: () => void }[] = [];
  if (statusFilter !== 'all') activeFilterChips.push({ id: 'status', label: `Status: ${statusFilter}`, clear: () => setStatusFilter('all') });
  if (planFilter !== 'all') activeFilterChips.push({ id: 'plan', label: `Plan: ${tierLabel(planFilter)}`, clear: () => setPlanFilter('all') });
  if (timeframeFilter !== 'all') activeFilterChips.push({ id: 'time', label: `Time: ${timeframeFilter}`, clear: () => setTimeframeFilter('all') });
  if (syncFilter !== 'all') activeFilterChips.push({ id: 'sync', label: `Sync: ${syncFilter}`, clear: () => setSyncFilter('all') });
  if (cancelScheduled) activeFilterChips.push({ id: 'cancel', label: 'Cancel at period end', clear: () => setCancelScheduled(false) });
  if (endingSoon) activeFilterChips.push({ id: 'soon', label: 'Renews ≤7d', clear: () => setEndingSoon(false) });
  if (query.trim()) activeFilterChips.push({ id: 'q', label: `Search: ${query.trim()}`, clear: () => setQuery('') });

  const showMyPlanSection = !showAdminRevenue || planRows.length > 0 || Boolean(currentTierLabel);

  return (
    <div className="billing-console">
      <header className="billing-console__hero">
        <div>
          <p className="billing-console__eyebrow">
            {resolvedAudience === 'operator'
              ? (language === 'es' ? 'Ingresos entrantes' : 'Inbound revenue')
              : (language === 'es' ? 'Tu suscripción' : 'Your subscription')}
          </p>
          <p className="billing-console__lede">
            {resolvedAudience === 'operator'
              ? (language === 'es'
                ? 'Suscripciones de clientes que pagan a NextCompli (Stripe).'
                : 'Customer subscriptions that pay NextCompli (Stripe).')
              : (language === 'es'
                ? 'Plan actual, mejoras y cancelación a través de Stripe.'
                : 'Current plan, upgrades, and cancellation via Stripe.')}
          </p>
        </div>
        <div className="billing-console__actions">
          <button type="button" className="dashboard-btn dashboard-btn-primary" onClick={() => void openPortal()} disabled={portalBusy || !accessToken}>
            {portalBusy
              ? (language === 'es' ? 'Abriendo…' : 'Opening…')
              : (language === 'es' ? 'Mejorar o cancelar plan' : 'Upgrade or cancel plan')}
          </button>
          <button type="button" className="dashboard-btn" onClick={() => void load()}>
            {language === 'es' ? 'Actualizar' : 'Refresh'}
          </button>
          {showAdminRevenue ? (
            <Link to="/console/subscriptions" className="dashboard-btn">
              {language === 'es' ? 'Proveedores de pago' : 'Paid vendors'}
            </Link>
          ) : null}
        </div>
      </header>

      <section
        className={`billing-console__plans-accordion${plansOpen ? ' is-open' : ''}`}
        aria-label={language === 'es' ? 'Planes disponibles' : 'Available plans'}
      >
        <button
          type="button"
          className="billing-console__plans-toggle"
          aria-expanded={plansOpen}
          aria-controls="billing-plans-accordion-panel"
          onClick={() => setPlansOpen((open) => !open)}
        >
          <span>
            <strong>{language === 'es' ? 'Comparar planes' : 'Compare plans'}</strong>
            <span className="billing-console__plans-hint">
              {language === 'es'
                ? 'CompliCore, CompliAdvantage y CompliComplete'
                : 'CompliCore, CompliAdvantage, and CompliComplete'}
            </span>
          </span>
          <span className="billing-console__chevron" aria-hidden="true">{plansOpen ? '▾' : '▸'}</span>
        </button>
        {plansOpen ? (
          <div id="billing-plans-accordion-panel" className="billing-console__plans-panel" role="region">
            <div className="billing-console__plans-grid">
              {PRICING_TIERS.map((tier) => {
                const isCurrent = currentTierId === tier.id;
                const ctaLabel = isCurrent
                  ? (language === 'es' ? 'Plan actual' : 'Current plan')
                  : tier.id === 'enterprise'
                    ? (language === 'es' ? 'Solicitar cotización' : 'Contact for quote')
                    : (language === 'es' ? 'Mejorar' : 'Upgrade');
                return (
                  <article
                    key={tier.id}
                    className={`billing-console__plan-card${isCurrent ? ' is-current' : ''}`}
                  >
                    <h4>{tier.marketingLabel}</h4>
                    <p className="billing-console__plan-price">
                      {tier.priceDisplay
                        ? `${tier.priceDisplay}${language === 'es' ? '/mes' : '/mo'}`
                        : (language === 'es' ? 'Cotización' : 'Contact for quote')}
                    </p>
                    <p className="billing-console__plan-subtitle">{tier.subtitle}</p>
                    <ul>
                      {tier.includes.slice(0, 6).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`dashboard-btn billing-console__plan-cta${isCurrent ? '' : ' dashboard-btn-primary'}`}
                      disabled={isCurrent || upgradeBusyId === tier.id || portalBusy}
                      onClick={() => void startTierAction(tier.id)}
                    >
                      {upgradeBusyId === tier.id
                        ? (language === 'es' ? 'Abriendo…' : 'Opening…')
                        : ctaLabel}
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        ) : null}
      </section>

      {showAdminRevenue ? (
      <section className="billing-console__switcher" aria-label={language === 'es' ? 'Otras secciones de facturación' : 'Related billing sections'}>
        <Link to="/console/subscriptions" className="billing-console__switcher-card">
          <strong>{language === 'es' ? 'Proveedores de pago' : 'Paid vendors'}</strong>
          <span>
            {language === 'es'
              ? 'Cuentas y credenciales de servicios que NextCompli paga (AWS, datos, SaaS).'
              : 'Accounts and credentials for services NextCompli pays for (AWS, data, SaaS).'}
          </span>
        </Link>
        <Link to="/console/payments" className="billing-console__switcher-card">
          <strong>{language === 'es' ? 'Métodos de pago' : 'Payment methods'}</strong>
          <span>
            {language === 'es'
              ? 'Tarjetas, facturas y preferencias de cobro de la cuenta.'
              : 'Cards, invoices, and account payment preferences.'}
          </span>
        </Link>
      </section>
      ) : (
      <section className="billing-console__switcher" aria-label={language === 'es' ? 'Acciones de suscripción' : 'Subscription actions'}>
        <button type="button" className="billing-console__switcher-card" onClick={() => void openPortal()} disabled={portalBusy || !accessToken}>
          <strong>{language === 'es' ? 'Mejorar plan' : 'Upgrade plan'}</strong>
          <span>
            {language === 'es'
              ? 'Cambia de CompliCore a CompliAdvantage en el portal de Stripe (tarjeta de prueba).'
              : 'Move from CompliCore to CompliAdvantage in the Stripe portal (test card).'}
          </span>
        </button>
        <button type="button" className="billing-console__switcher-card" onClick={() => void openPortal()} disabled={portalBusy || !accessToken}>
          <strong>{language === 'es' ? 'Cancelar suscripción' : 'Cancel subscription'}</strong>
          <span>
            {language === 'es'
              ? 'Programa la cancelación al final del periodo de facturación.'
              : 'Schedule cancellation at the end of the billing period.'}
          </span>
        </button>
      </section>
      )}

      {error && <p className="dashboard-form-error" role="alert">{error}</p>}

      {showMyPlanSection && (
        <section className="billing-console__card" aria-label={language === 'es' ? 'Tu plan' : 'Your plan'}>
          <div className="billing-console__card-head">
            <h3>{language === 'es' ? 'Tu plan' : 'Your plan'}</h3>
          </div>
          {planRows.length === 0 ? (
            currentTierLabel ? (
              <ul className="billing-console__plan-list">
                <li>
                  <div>
                    <strong>{currentTierLabel}</strong>
                    <span>
                      {language === 'es'
                        ? 'Activo vía entitlements de la empresa'
                        : 'Active via company entitlements'}
                    </span>
                  </div>
                  <span className="billing-console__status billing-console__status--active">active</span>
                </li>
              </ul>
            ) : (
              <p className="billing-console__empty">
                {language === 'es'
                  ? 'Esta cuenta de inicio de sesión no tiene una suscripción Stripe propia. '
                  : 'This signed-in account has no personal Stripe subscription. '}
                <button type="button" className="billing-console__inline-link" onClick={() => setPlansOpen(true)}>
                  {language === 'es' ? 'Ver planes' : 'View plans'}
                </button>
                {showAdminRevenue
                  ? (language === 'es'
                    ? ' — normal para operadores; usa Ingresos de clientes abajo.'
                    : ' — normal for operators; use Customer revenue below.')
                  : '.'}
              </p>
            )
          ) : (
            <ul className="billing-console__plan-list">
              {planRows.map((row) => (
                <li key={row.subscription_id}>
                  <div>
                    <strong>{tierLabel(row.tier_id)}</strong>
                    <span>{renewalLabel(row, language)}</span>
                  </div>
                  <span className={`billing-console__status billing-console__status--${String(row.status || 'unknown').toLowerCase()}`}>
                    {row.status || 'unknown'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {showAdminRevenue && !showMyPlanSection && (
        <p className="billing-console__operator-note">
          {language === 'es'
            ? 'Vista de operador: no hay plan personal en esta cuenta de admin — se muestran los clientes que nos pagan.'
            : 'Operator view: this admin account has no personal plan — showing customers who pay us.'}
        </p>
      )}

      {showAdminRevenue && (
        <section className="billing-console__card" aria-label={language === 'es' ? 'Ingresos de clientes' : 'Customer revenue'}>
          <div className="billing-console__card-head">
            <div>
              <h3>{language === 'es' ? 'Ingresos de clientes' : 'Customer revenue'}</h3>
              <p className="billing-console__meta">
                {summary
                  ? `${summary.total ?? rows.length} total · ${summary.active ?? 0} active · ${summary.past_due ?? 0} past due · showing ${filteredAdminRows.length}`
                  : `${rows.length} subscriptions`}
              </p>
            </div>
            <button type="button" className="dashboard-btn" onClick={exportCsv} disabled={filteredAdminRows.length === 0}>
              CSV
            </button>
          </div>

          <div className="billing-console__toolbar">
            <input
              type="search"
              className="billing-console__search"
              placeholder={language === 'es' ? 'Buscar empresa, email o ID…' : 'Search company, email, or Stripe ID…'}
              value={query}
              onChange={(event) => { setActiveInsightId(null); setQuery(event.target.value); }}
            />
            <button
              type="button"
              className={`dashboard-btn ${filtersOpen ? 'dashboard-btn-primary' : ''}`}
              onClick={() => setFiltersOpen((open) => !open)}
            >
              {language === 'es' ? 'Filtros' : 'Filters'}
              {activeFilterChips.length > 0 ? ` (${activeFilterChips.length})` : ''}
            </button>
          </div>

          {smartFilters.length > 0 && (
            <div className="billing-console__insights">
              <span>{language === 'es' ? 'Sugerencias' : 'Suggestions'}</span>
              {smartFilters.slice(0, 4).map((insight) => (
                <button
                  key={insight.id}
                  type="button"
                  className={activeInsightId === insight.id ? 'is-active' : ''}
                  title={insight.reason}
                  onClick={() => applyInsight(insight)}
                >
                  {insight.label}
                </button>
              ))}
            </div>
          )}

          {activeFilterChips.length > 0 && (
            <div className="billing-console__chips">
              {activeFilterChips.map((chip) => (
                <button key={chip.id} type="button" onClick={chip.clear}>
                  {chip.label} ×
                </button>
              ))}
              <button type="button" className="billing-console__clear" onClick={clearFilters}>
                {language === 'es' ? 'Limpiar' : 'Clear all'}
              </button>
            </div>
          )}

          {filtersOpen && (
            <div className="billing-console__filter-panel">
              <label>
                {language === 'es' ? 'Estado' : 'Status'}
                <select value={statusFilter} onChange={(e) => { setActiveInsightId(null); setStatusFilter(e.target.value); }}>
                  {['all', 'active', 'past_due', 'trialing', 'canceled', 'unpaid'].map((value) => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
              </label>
              <label>
                Plan
                <select value={planFilter} onChange={(e) => { setActiveInsightId(null); setPlanFilter(e.target.value); }}>
                  <option value="all">all</option>
                  <option value="basic">CompliCore</option>
                  <option value="advanced">CompliAdvantage</option>
                  <option value="enterprise">CompliComplete</option>
                </select>
              </label>
              <label>
                {language === 'es' ? 'Tiempo' : 'Timeframe'}
                <select
                  value={timeframeFilter}
                  onChange={(e) => {
                    setActiveInsightId(null);
                    setEndingSoon(false);
                    setTimeframeFilter(e.target.value);
                  }}
                >
                  <option value="all">any</option>
                  <option value="created_7d">signed up 7d</option>
                  <option value="created_30d">signed up 30d</option>
                  <option value="renew_7d">renews 7d</option>
                  <option value="renew_30d">renews 30d</option>
                  <option value="renew_this_month">renews this month</option>
                </select>
              </label>
              <label>
                Sync
                <select value={syncFilter} onChange={(e) => { setActiveInsightId(null); setSyncFilter(e.target.value); }}>
                  <option value="all">all</option>
                  <option value="synced">synced</option>
                  <option value="dunning">dunning</option>
                  <option value="revoked">revoked</option>
                  <option value="attention">needs attention</option>
                </select>
              </label>
              <label className="billing-console__check">
                <input
                  type="checkbox"
                  checked={cancelScheduled}
                  onChange={(e) => { setActiveInsightId(null); setCancelScheduled(e.target.checked); }}
                />
                {language === 'es' ? 'Cancela al fin del periodo' : 'Cancel at period end'}
              </label>
              <label className="billing-console__check">
                <input
                  type="checkbox"
                  checked={endingSoon}
                  onChange={(e) => {
                    setActiveInsightId(null);
                    setEndingSoon(e.target.checked);
                    if (e.target.checked) setTimeframeFilter('all');
                  }}
                />
                {language === 'es' ? 'Renueva en ≤7 días' : 'Renews within 7 days'}
              </label>
            </div>
          )}

          {filteredAdminRows.length === 0 ? (
            <p className="billing-console__empty">
              {language === 'es' ? 'Ningún cliente coincide con estos filtros.' : 'No customers match these filters.'}
            </p>
          ) : (
            <div className="billing-console__table-wrap">
              <table className="billing-console__table">
                <thead>
                  <tr>
                    <th>{language === 'es' ? 'Cliente' : 'Customer'}</th>
                    <th>Plan</th>
                    <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                    <th>{language === 'es' ? 'Renovación' : 'Renewal'}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredAdminRows.map((row) => (
                    <tr key={row.subscription_id}>
                      <td>
                        <strong>{row.company_name || (row.company_id != null ? `Company #${row.company_id}` : 'Unknown')}</strong>
                        <div className="billing-console__sub">{row.email || '—'}</div>
                      </td>
                      <td>{tierLabel(row.tier_id)}</td>
                      <td>
                        <span className={`billing-console__status billing-console__status--${String(row.status || 'unknown').toLowerCase()}`}>
                          {row.status || 'unknown'}
                        </span>
                      </td>
                      <td>
                        {renewalLabel(row, language)}
                        {row.entitlement_sync_status && row.entitlement_sync_status !== 'synced' ? (
                          <div className="billing-console__sub">sync: {row.entitlement_sync_status}</div>
                        ) : null}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="dashboard-security-inline-btn"
                          disabled={syncBusyId === row.subscription_id}
                          onClick={() => void syncEntitlements(row.subscription_id)}
                        >
                          {syncBusyId === row.subscription_id ? '…' : 'Re-sync'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default CustomerBillingPanel;
